import {
  UMButton, UMFormStyles, UMIcon, UMR, UMSidePanel, UMStack, UMToolbarIconButton,
  type UMSelectionSummary,
} from '../_components';
import type { ConsumptionType, SelectionInfo, SelectionTool } from './types';
import { ProdControlStyles } from './ProdControls';
import { NetworkControls } from './NetworkControls';
import type { useNetwork } from './useNetwork';
import { BuildingUpload, useBuildingUpload } from './BuildingUpload';
import { NetworkLegend } from './NetworkLegend';
import { PlantControls } from './PlantControls';
import { DensityControls } from './DensityControls';
import { SelectionSection } from './SelectionSection';
import { SelectionActions } from './SelectionActions';

// Variante locale de UMSelectionPanel : même géométrie, footer et verrouillage
// confirmés par HomeSidebarSelection*.vue. Aucun changement de _components.

// Sous-titres de la section « Canalisations » : jamais plus gros que le titre de section (14px).
const SUBTITLE = { fontSize: 14, lineHeight: '20px', fontWeight: 600, color: UMR.textStrong } as const;

function SummaryRow({ label, value, unit }: { label: string; value: number; unit?: string }) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
      <div style={{ fontSize: 14, lineHeight: '20px', color: UMR.textStrong }}>{label}</div>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', lineHeight: 1.25 }}>
        <div style={{ fontSize: 16, lineHeight: '24px', fontWeight: 700, color: UMR.textStrong }}>
          {value.toLocaleString('fr-FR')}
        </div>
        {unit && <div style={{ fontSize: 12, lineHeight: UMR.lhXs, color: UMR.textMuted }}>{unit}</div>}
      </div>
    </div>
  );
}

export function SelectionPanel({ upload, network, onClose, summary, activeTool, selections, selectionId, selectionLabel, consumption, onSelectionChange, onSelectionActions, onQuit, onCreate, onEditLegend }: {
  upload: ReturnType<typeof useBuildingUpload>;
  network: ReturnType<typeof useNetwork>;
  onSelectionActions: (message: string) => void;
  onQuit: () => void;
  onCreate: () => void;
  onEditLegend: () => void;
  selections: SelectionInfo[];
  /** null = aucune sélection courante (après « Quitter la sélection »). */
  selectionId: string | null;
  selectionLabel: string;
  consumption: ConsumptionType;
  onSelectionChange: (id: string) => void;
  onClose: () => void;
  summary: UMSelectionSummary;
  activeTool: SelectionTool | null;
}) {
  if (!selectionId) {
    return (
      <UMSidePanel title="Sélection" onClose={onClose}>
        <UMFormStyles />
        <ProdControlStyles />
        <UMStack gap={16}>
          <select aria-label="Ouvrir une sélection" className="prod-control" value=""
            onChange={(event) => onSelectionChange(event.target.value)}
            style={{
              width: '100%', height: 32, padding: '0 16px', boxSizing: 'border-box',
              border: `1px solid ${UMR.border}`, borderRadius: 0,
              background: UMR.white, color: UMR.textStrong, fontFamily: UMR.font, fontSize: 14,
            }}
          >
            <option value="" disabled hidden>Ouvrir une sélection</option>
            {selections.map((s) => <option key={s.id} value={s.id} style={{ color: UMR.textStrong }}>{s.name}</option>)}
          </select>
          <div style={{ fontSize: 14, lineHeight: '21px', color: UMR.textStrong }}>ou</div>
          <button type="button" onClick={onCreate} className="um-hover-grey" style={{
            alignSelf: 'flex-start', height: 32, boxSizing: 'border-box', padding: '0 16px',
            display: 'inline-flex', alignItems: 'center', gap: 8, background: UMR.white,
            border: `1px solid ${UMR.blue}`, borderRadius: 0, color: UMR.blue,
            fontFamily: UMR.font, fontSize: 14, cursor: 'pointer',
          }}>
            <UMIcon name="create_new_folder" size={20} color={UMR.blue} />
            Créer une sélection
          </button>
        </UMStack>
      </UMSidePanel>
    );
  }
  return (
    <UMSidePanel
      title="Sélection de batiments"
      onClose={onClose}
      footer={network.activeTool ? <>
        <UMButton variant="secondary" onClick={network.cancel}>Annuler</UMButton>
        <UMButton disabled={!network.canValidate} onClick={network.validate}>
          {network.activeTool === 'draw-pipe' ? 'Terminer et valider' : 'Valider'}
        </UMButton>
      </> : undefined}
    >
      <UMFormStyles />
      <ProdControlStyles />
      <div style={{ position: 'absolute', top: 8, right: 36, zIndex: 1 }}>
        <SelectionActions key={selectionId} onMessage={onSelectionActions} />
      </div>
      <UMStack gap={16}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{ position: 'relative', flex: 1, minWidth: 0, height: 40, boxSizing: 'border-box', border: `1px solid ${UMR.border}`, display: 'flex', alignItems: 'center', padding: '0 4px 0 8px', gap: 4 }}>
            <span style={{ position: 'absolute', top: -9, left: 8, padding: '0 4px', background: UMR.white, fontSize: 12, lineHeight: '16px', color: UMR.textLabel }}>
              Sélection en cours
            </span>
            <span aria-label="Sélection active" style={{
              display: 'inline-flex', alignItems: 'center', gap: 4, minWidth: 0, height: 26, boxSizing: 'border-box', padding: '0 4px 0 8px',
              background: UMR.bluePressed, border: `1px solid ${UMR.blue}`, color: UMR.blueLink, fontSize: 14,
            }}>
              <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{selectionLabel}</span>
              <button type="button" aria-label="Quitter la sélection" title="Quitter la sélection" onClick={onQuit}
                style={{ display: 'flex', border: 'none', padding: 0, background: 'transparent', color: UMR.blueLink, cursor: 'pointer' }}>
                <UMIcon name="close" size={14} />
              </button>
            </span>
            <span style={{ marginLeft: 'auto', position: 'relative', width: 28, height: 28, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <UMIcon name="keyboard_arrow_down" size={20} />
              <select id="current-selection" aria-label="Changer de sélection" value={selectionId}
                onChange={(event) => onSelectionChange(event.target.value)}
                disabled={!!activeTool || !!network.activeTool || network.loading}
                style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', opacity: 0, cursor: 'pointer' }}>
                {selections.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </span>
          </div>
          <UMToolbarIconButton icon="create_new_folder" label="Créer une sélection" onClick={onCreate} />
        </div>
        {selectionId && <>
        <div style={{
          display: 'flex', flexDirection: 'column', gap: 12, padding: '8px 16px',
          background: UMR.grey100, borderRadius: 8, border: `1px solid ${UMR.border}`,
        }}>
          <div style={{ fontSize: 12, lineHeight: UMR.lhXs, fontWeight: 500, color: UMR.textLabel }}>Résumé</div>
          <UMStack gap={16}>
            <SummaryRow label="Nombre de bâtiments" value={summary.buildings} />
            <SummaryRow label={consumption === 'Froid' ? 'Total froid' : 'Total chaud'} value={summary.heatTotal} unit="MWhu" />
            <SummaryRow label="Canalisations" value={summary.pipes} unit="ml" />
          </UMStack>
        </div>
        <div>
          <SelectionSection title="Bâtiments" defaultOpen>
            <BuildingUpload state={upload.files[selectionId]} disabled={!!activeTool || !!network.activeTool || network.loading}
              onChoose={(name) => upload.choose(selectionId, name)} />
          </SelectionSection>
          <SelectionSection title="Centrales de production">
            <PlantControls network={network} blocked={!!activeTool} />
          </SelectionSection>
          <SelectionSection title="Canalisations">
            <UMStack gap={16}>
              <NetworkLegend density={network.density} visible={network.pipesVisible} onVisibilityChange={network.setPipesVisible}
                onEditLegend={onEditLegend} />
              <UMStack gap={8}>
                <div style={SUBTITLE}>Tracé de réseau</div>
                <NetworkControls network={network} blocked={!!activeTool} />
              </UMStack>
              <UMStack gap={8}>
                <div style={SUBTITLE}>Densité linéaire</div>
                <DensityControls density={network.density} blocked={!!activeTool || !!network.activeTool || network.loading} />
              </UMStack>
            </UMStack>
          </SelectionSection>
        </div>
        </>}
      </UMStack>
    </UMSidePanel>
  );
}
